import { createContext, useContext } from 'react';
import { XrinvData } from '../types/comms-check';

export interface DecommissionASiteInputs {
  mpxn: string;
  onsiteRadioAnswer: string;
  chfDeviceId: string;
}

interface DecommissionASiteInputsContextProps {
  decommissionASiteInputs: DecommissionASiteInputs;
  setDecommissionASiteInputs: (decommissionASiteInputs: DecommissionASiteInputs) => void;
}

export const DecommissionASiteInputsInitialState: DecommissionASiteInputs = {
  mpxn: '',
  onsiteRadioAnswer: '',
  chfDeviceId: '',
};

export const DecommissionASiteInputsContext = createContext<DecommissionASiteInputsContextProps | null>({
  decommissionASiteInputs: DecommissionASiteInputsInitialState,
  setDecommissionASiteInputs: () => {},
});

export const useDecommissionASiteInputsContext = (): DecommissionASiteInputsContextProps => {
  const context = useContext(DecommissionASiteInputsContext);
  if (!context) {
    throw new Error('useDecommissionASiteInputsContext must be used within a DecommissionASiteInputsProvider');
  }
  return context;
};

export enum DecommissionASiteScreenType {
  INITIAL,
  CHECK_DEVICES,
  DEVICES_LIST,
  DECOMMISSION_IN_PROGRESS,
  DECOMMISSION_RESULT,
}

interface DecommissionASiteScreenContextProps {
  screen: DecommissionASiteScreenType;
  setScreen: (screen: DecommissionASiteScreenType) => void;
}

export const DecommissionASiteScreenContext = createContext<DecommissionASiteScreenContextProps>({
  screen: DecommissionASiteScreenType.INITIAL,
  setScreen: () => {},
});

export const useDecommissionASiteScreenContext = (): DecommissionASiteScreenContextProps => {
  const context = useContext(DecommissionASiteScreenContext);
  if (!context) {
    throw new Error('useDecommissionASiteScreenContext must be used within a DecommissionASiteScreenProvider');
  }
  return context;
};

export type DecommissionASiteSummaryType = {
  inventoryData: DecommissionASiteInventoryCheckType;
  decommissionHandler: () => void;
};

export type DecommissionASiteResultScreenType = {
  decommissionSiteResult: DecommissionASiteResultType;
};

export enum DecommissionASiteResultStatus {
  PROCESSING = 'PROCESSING',
  FAIL = 'FAIL',
  SUCCESS = 'SUCCESS',
  TIMEOUT = 'TIMEOUT',
  NOT_DEFINED = 'NOT_DEFINED',
}

export type DecommissionASiteResultType = {
  status: DecommissionASiteResultStatus | null;
  chfDeviceId: string;
  esmeDeviceId: string;
  gsmeDeviceId: string;
};

export const INITIAL_DECOMMISSION_SITE_RESULT: DecommissionASiteResultType = {
  status: null,
  chfDeviceId: '',
  esmeDeviceId: '',
  gsmeDeviceId: '',
};

export type DecommissionASiteInventoryCheckType = {
  address: string;
  data: XrinvData[];
  state: 'PROCESSING' | 'FAIL' | 'SUCCESS' | 'PROXY_L1_ERROR' | 'PROXY_L2_ERROR' | null;
};

export type ReportingDataType = {
  decommissionJourneyId: string;
  inventoryCheckStartingTimeStamp: Date;
  siteDecommissionStartingTimeStamp: Date;
};
